import { useCallback, useEffect, useState } from "react";
import { Lock, Unlock } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

/** OTP gate: every account or lab change needs a fresh emailed code, valid for 10 minutes. */
export const AdminSecurityCard = ({ onChange }) => {
  const [state, setState] = useState({ unlocked: false, expires_at: null, minutes: 10 });
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/security/unlock");
      setState(data);
      onChange?.(data.unlocked);
    } catch (e) { console.warn("unlock status unavailable:", e); }
  }, [onChange]);

  useEffect(() => { refresh(); const t = setInterval(refresh, 30000); return () => clearInterval(t); },
    [refresh]);

  const request = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/security/otp/request");
      setSent(true);
      toast.success(`Confirmation code sent to ${data.sent_to}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/security/otp/verify", { code: code.trim() });
      setCode(""); setSent(false);
      toast.success(`User and lab administration unlocked for ${data.minutes} minutes`);
      await refresh();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const lock = async () => {
    try { await api.post("/admin/security/lock"); await refresh(); toast.success("Locked again"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const until = state.expires_at ? new Date(state.expires_at).toLocaleTimeString() : null;

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-5" data-testid="admin-security-card">
      <div className="flex items-start gap-3">
        {state.unlocked ? <Unlock className="h-5 w-5 text-[#2E7D32] mt-0.5" />
          : <Lock className="h-5 w-5 text-[#C92A2A] mt-0.5" />}
        <div>
          <h2 className="font-heading font-bold text-[#111827]">Security confirmation</h2>
          <p className="text-xs text-[#6B7280] mt-0.5">
            Creating, editing, linking, de-linking or deleting a user or a lab needs a code emailed to you.
            One code unlocks these actions for {state.minutes} minutes.
          </p>
        </div>
      </div>

      <div className="mt-4">
        {state.unlocked ? (
          <div className="flex flex-wrap items-center gap-3" data-testid="admin-unlocked">
            <span className="text-xs text-[#2E7D32] font-medium">
              Unlocked{until ? ` until ${until}` : ""}
            </span>
            <Button variant="outline" size="sm" onClick={lock} data-testid="admin-lock-btn">Lock now</Button>
          </div>
        ) : (
          <form onSubmit={verify} className="flex flex-wrap items-end gap-2" data-testid="admin-locked">
            <Button type="button" onClick={request} disabled={busy} data-testid="admin-otp-request-btn"
              className="bg-[#002FA7] hover:bg-[#00237C] text-white">
              {sent ? "Resend code" : "Email me a code"}
            </Button>
            <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
              className="w-40" data-testid="admin-otp-code-input" />
            <Button type="submit" variant="outline" disabled={busy || code.trim().length < 4}
              data-testid="admin-otp-verify-btn">Confirm</Button>
          </form>
        )}
      </div>
    </div>
  );
};
